// ============================================================
// TenantAuth.js — Tenant portal session, auth & fetch utilities
// Fully connected to Azure Web API (https://zangon-web-api.azurewebsites.net)
// ============================================================
import { applyBillingEnumText, enumOf } from "../../utils/enums";

export const PRODUCTION_API_URL = "https://zangon-web-api.azurewebsites.net";
const DEV_PROXY_BASE = "/api";

// Use dev proxy /api in development to prevent CORS, production url otherwise
export const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PRODUCTION_API_URL);

export const TENANT_LOGIN_ENDPOINT = `${API_BASE}/auth/tenant-login`;
export const TENANT_SESSION_KEY    = "zangon_tenant_session";
export const LAST_ESTATE_KEY       = "zangon_estate_id";

// ─── E.164 Phone Normalisation ─────────────────────────────────
/**
 * Normalises phone numbers to E.164 (+234...).
 * Strips spaces, dashes, parentheses. Converts leading 0 to +234.
 */
export function normalizePhoneNumber(phone = "") {
  let cleaned = String(phone).replace(/[\s\-()]/g, "").trim();
  if (!cleaned) return "";
  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("234")) return `+${cleaned}`;
  if (cleaned.startsWith("0")) return `+234${cleaned.slice(1)}`;
  if (/^\d{10}$/.test(cleaned)) return `+234${cleaned}`;
  return cleaned;
}

// ─── Password Rules Validator ──────────────────────────────────
/**
 * Validates: Min 6 characters, 1 uppercase, 1 number, 1 special character.
 * Returns null if valid, or an error message string.
 */
export function validatePassword(password = "") {
  if (!password || password.length < 6) {
    return "Password must be at least 6 characters long.";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter (A-Z).";
  }
  if (!/[0-9]/.test(password)) {
    return "Password must contain at least one number (0-9).";
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must contain at least one special character (!@#$%^&*...).";
  }
  return null;
}

// ─── Helpers ───────────────────────────────────────────────────
export function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

/**
 * 1.4 Standard Error Response Schema Extractor
 * Formats 400 validation failures (errors: { field: [msg] }) by joining messages into one string.
 * Falls back to message or error or fallback string.
 */
export function extractErrorMessage(json, fallback = "Something went wrong.") {
  if (!json) return fallback;
  if (typeof json === "string") return json;
  if (json.errors && typeof json.errors === "object") {
    const messages = [];
    for (const key of Object.keys(json.errors)) {
      const val = json.errors[key];
      if (Array.isArray(val)) messages.push(...val);
      else if (typeof val === "string") messages.push(val);
    }
    if (messages.length > 0) return messages.join(" • ");
  }
  return json.message || json.error || fallback;
}

export function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.data)) return raw.data;
  }
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(json.items)) return json.items;
  if (typeof raw === "string") {
    const p = safeJsonParse(raw);
    if (Array.isArray(p)) return p;
    if (p && typeof p === "object") {
      if (Array.isArray(p.items)) return p.items;
      if (Array.isArray(p.data)) return p.data;
    }
  }
  if (Array.isArray(json)) return json;
  return [];
}

// ─── Dependent type ────────────────────────────────────────────
// API enum: 0 Family, 1 Staff, 2 Visitor, 3 Delivery Person, 4 Service Person.
export function toDependentType(value, fallback = 0) {
  if (typeof value === "number") return value;
  const dt = String(value || "").toLowerCase();
  if (!dt) return fallback;
  if (/family|relative|child|spouse|wife|husband|son|daughter/.test(dt)) return 0;
  if (/staff|maid|house|driver|cook|nanny|security|cleaner/.test(dt)) return 1;
  if (/visitor|guest|friend/.test(dt)) return 2;
  if (/delivery|courier|dispatch/.test(dt)) return 3;
  if (/service|artisan|tech|plumb|elect|carpenter|painter/.test(dt)) return 4;
  return fallback;
}

// ─── Maintenance priority ──────────────────────────────────────
// API enum: 0 = Low, 1 = Normal, 2 = High, 3 = Emergency. The UI shows Low / Medium / High / Critical.
export const PRIORITY_LABELS = ["Low", "Medium", "High", "Critical"];

export function normalizePriority(value) {
  if (typeof value === "number" && PRIORITY_LABELS[value]) return PRIORITY_LABELS[value];
  const p = String(value ?? "").trim().toLowerCase();
  if (/^\d$/.test(p) && PRIORITY_LABELS[Number(p)]) return PRIORITY_LABELS[Number(p)];
  if (p === "low") return "Low";
  if (p === "high") return "High";
  if (p === "critical" || p === "emergency") return "Critical";
  return "Medium";
}

// ─── Session Management ────────────────────────────────────────
export function getTenantSession() {
  try { return JSON.parse(localStorage.getItem(TENANT_SESSION_KEY) || "null"); } catch { return null; }
}

export function setTenantSession(session) {
  if (!session) return;
  if (session.estateId) {
    localStorage.setItem(LAST_ESTATE_KEY, session.estateId);
  }
  localStorage.setItem(TENANT_SESSION_KEY, JSON.stringify(session));
}

export function clearTenantSession() {
  localStorage.removeItem(TENANT_SESSION_KEY);
  localStorage.removeItem("zangon_tenant_remember_me");
  sessionStorage.removeItem("zangon_tenant_session_active");
}

export function getTenantAccessToken()  { return getTenantSession()?.accessToken  || ""; }
export function getTenantRefreshToken() { return getTenantSession()?.refreshToken || ""; }

export function getTenantId() {
  const s = getTenantSession();
  return s?.tenantId || s?.userId || s?.sub || s?.primaryOccupantId || s?.id || "";
}

export function getTenantEstateId() {
  return getTenantSession()?.estateId || localStorage.getItem(LAST_ESTATE_KEY) || "";
}

export function isPrimaryOccupant() {
  const s = getTenantSession();
  const ut = (s?.userType || s?.role || "").toLowerCase();
  return ut.includes("primary") || !!s?.primaryOccupantId;
}

// ─── Timeout Fetch (Auth endpoints bypass Bearer) ──────────────
export async function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: ctrl.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Please try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

// ───────────────────────────────────────────────────────────────
// 2.1 AUTHENTICATION ENDPOINTS
// ───────────────────────────────────────────────────────────────

/**
 * Email is sent as-is; anything else is treated as a phone number and normalised to E.164.
 */
export function normalizeEmailOrPhone(value = "") {
  const clean = String(value).trim();
  return clean.includes("@") ? clean : normalizePhoneNumber(clean);
}

/**
 * 2.1.1 Tenant Login (password)
 * POST /auth/tenant-login
 * Body: { emailOrPhone: "user@example.com" | "+234...", password: "..." }
 */
export async function tenantLogin({ emailOrPhone, password, rememberMe = true }) {
  const res = await fetchWithTimeout(`${API_BASE}/auth/tenant-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ emailOrPhone: normalizeEmailOrPhone(emailOrPhone), password }),
  }, 30000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    throw new Error(json?.message || json?.error || text || `Login failed (${res.status})`);
  }

  const session = json?.data;
  if (!session?.accessToken) throw new Error("Login succeeded but no access token was returned.");

  session.userType = session.userType || "tenant";
  localStorage.setItem("zangon_tenant_remember_me", rememberMe ? "true" : "false");
  sessionStorage.setItem("zangon_tenant_session_active", "true");
  setTenantSession(session);

  return { ok: true, session, message: json?.message || "Login successful." };
}

/**
 * 2.1.2 Primary Occupant Login (password)
 * POST /auth/primary-occupant-login
 * Body: { emailOrPhone: "...", password: "..." }
 * Forces userType = "primary-occupant" in storage.
 */
export async function primaryOccupantLogin({ emailOrPhone, password, rememberMe = true }) {
  const identifier = emailOrPhone.includes("@") ? emailOrPhone.trim() : normalizePhoneNumber(emailOrPhone);

  const res = await fetchWithTimeout(`${API_BASE}/auth/primary-occupant-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ emailOrPhone: identifier, password }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    throw new Error(json?.message || json?.error || text || `Login failed (${res.status})`);
  }

  const session = json?.data;
  if (!session?.accessToken) throw new Error("Login succeeded but no access token was returned.");

  session.userType = "primary-occupant";
  localStorage.setItem("zangon_tenant_remember_me", rememberMe ? "true" : "false");
  sessionStorage.setItem("zangon_tenant_session_active", "true");
  setTenantSession(session);

  return { ok: true, session, message: json?.message || "Login successful." };
}

/**
 * Unified Login (Tenant or Primary Occupant)
 * Automatically detects whether identifier is email or phone number
 * and tries the appropriate endpoint, falling back to the other.
 */
export async function unifiedLogin({ identifier, password, rememberMe = true }) {
  const cleanId = String(identifier || "").trim();
  const isEmail = cleanId.includes("@");

  if (isEmail) {
    try {
      return await primaryOccupantLogin({ emailOrPhone: cleanId, password, rememberMe });
    } catch (errPo) {
      try {
        return await tenantLogin({ emailOrPhone: cleanId, password, rememberMe });
      } catch {
        throw errPo;
      }
    }
  }

  // If phone, try tenant first
  try {
    return await tenantLogin({ emailOrPhone: cleanId, password, rememberMe });
  } catch (errTenant) {
    try {
      return await primaryOccupantLogin({ emailOrPhone: cleanId, password, rememberMe });
    } catch {
      throw errTenant;
    }
  }
}

/**
 * 2.1.3 Tenant First-Login — Start
 * POST /auth/tenant-login/start
 * Body: { emailOrPhone, phoneNumber, estateId }
 * Response data: { tenantId, requiresOtpSetup, requiresPassword, otpExpiresAt, message }
 */
function firstLoginIdentity({ emailOrPhone, estateId }) {
  const id = normalizeEmailOrPhone(emailOrPhone);
  return {
    emailOrPhone: id,
    phoneNumber: id.includes("@") ? "" : id,
    estateId: (estateId || "").trim(),
  };
}

export async function tenantFirstLoginStart({ emailOrPhone, estateId }) {
  const res = await fetchWithTimeout(`${API_BASE}/auth/tenant-login/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(firstLoginIdentity({ emailOrPhone, estateId })),
  }, 30000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Request failed (${res.status})`));
  return json?.data || json;
}

/**
 * 2.1.4 Tenant First-Login — Verify OTP
 * POST /auth/tenant-login/verify-otp
 * Body: { emailOrPhone, phoneNumber, estateId, otp }
 * Response data: { tenantId, setupToken, expiresAt }
 */
export async function tenantFirstLoginVerifyOtp({ emailOrPhone, estateId, otp }) {
  const res = await fetchWithTimeout(`${API_BASE}/auth/tenant-login/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ ...firstLoginIdentity({ emailOrPhone, estateId }), otp: otp.trim() }),
  }, 30000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `OTP verification failed (${res.status})`));
  return json?.data || json;
}

/**
 * 2.1.5 Tenant First-Login — Set Password
 * POST /auth/tenant-login/set-password
 * Body: { tenantId, setupToken, newPassword, confirmNewPassword }
 * Response: AuthResponse
 */
export async function tenantFirstLoginSetPassword({ tenantId, setupToken, newPassword, confirmNewPassword, rememberMe = true }) {
  const ruleErr = validatePassword(newPassword);
  if (ruleErr) throw new Error(ruleErr);
  if (newPassword !== confirmNewPassword) throw new Error("Passwords do not match.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/tenant-login/set-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ tenantId, setupToken, newPassword, confirmNewPassword }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Set password failed (${res.status})`);

  const session = json?.data;
  if (session?.accessToken) {
    session.userType = session.userType || "tenant";
    localStorage.setItem("zangon_tenant_remember_me", rememberMe ? "true" : "false");
    sessionStorage.setItem("zangon_tenant_session_active", "true");
    setTenantSession(session);
  }
  return { ok: true, session, message: json?.message || "Password set successfully." };
}

/**
 * 2.1.6 Primary Occupant First-Login — Start
 * POST /auth/primary-occupant-login/start
 * Body: { phoneNumber: "+234..." }
 * Response data: { primaryOccupantId, requiresOtpSetup, requiresPassword }
 */
export async function primaryOccupantFirstLoginStart({ phoneNumber }) {
  const normPhone = normalizePhoneNumber(phoneNumber);
  const res = await fetchWithTimeout(`${API_BASE}/auth/primary-occupant-login/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ phoneNumber: normPhone }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Request failed (${res.status})`);
  return json?.data || json;
}

/**
 * 2.1.7 Primary Occupant First-Login — Verify OTP
 * POST /auth/primary-occupant-login/verify-otp
 * Body: { phoneNumber: "+234...", otp: "123456" }
 * Response data: { primaryOccupantId, setupToken }
 */
export async function primaryOccupantFirstLoginVerifyOtp({ phoneNumber, otp }) {
  const normPhone = normalizePhoneNumber(phoneNumber);
  const res = await fetchWithTimeout(`${API_BASE}/auth/primary-occupant-login/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ phoneNumber: normPhone, otp: otp.trim() }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `OTP verification failed (${res.status})`);
  return json?.data || json;
}

/**
 * 2.1.8 Primary Occupant First-Login — Set Password
 * POST /auth/primary-occupant-login/set-password
 * Body: { primaryOccupantId, setupToken, newPassword, confirmNewPassword }
 * Response: AuthResponse
 */
export async function primaryOccupantFirstLoginSetPassword({ primaryOccupantId, setupToken, newPassword, confirmNewPassword, rememberMe = true }) {
  const ruleErr = validatePassword(newPassword);
  if (ruleErr) throw new Error(ruleErr);
  if (newPassword !== confirmNewPassword) throw new Error("Passwords do not match.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/primary-occupant-login/set-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ primaryOccupantId, setupToken, newPassword, confirmNewPassword }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Set password failed (${res.status})`);

  const session = json?.data;
  if (session?.accessToken) {
    session.userType = "primary-occupant";
    localStorage.setItem("zangon_tenant_remember_me", rememberMe ? "true" : "false");
    sessionStorage.setItem("zangon_tenant_session_active", "true");
    setTenantSession(session);
  }
  return { ok: true, session, message: json?.message || "Password set successfully." };
}

/**
 * 2.1.9 Refresh Token
 * PATCH /auth/refresh
 * Body: { refreshToken: "..." }
 */
export async function refreshTenantToken() {
  const refreshToken = getTenantRefreshToken();
  if (!refreshToken) throw new Error("No refresh token. Please sign in again.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ refreshToken }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || `Token refresh failed (${res.status})`);

  const prev = getTenantSession() || {};
  const incoming = json?.data ?? json ?? {};
  const next = { ...prev, ...incoming };
  setTenantSession(next);
  return next;
}

/**
 * 2.1.10 Logout
 * POST /auth/logout
 * Auth: Bearer token (Timeout: 5 seconds)
 * Swallows timeout or network errors; always clears local session.
 */
export async function tenantLogout() {
  const token = getTenantAccessToken();
  const refreshToken = getTenantRefreshToken();

  if (token) {
    try {
      await fetchWithTimeout(`${API_BASE}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ refreshToken }),
      }, 5000);
    } catch (err) {
      // Swallowed per specification (timeout / network error)
      console.warn("Logout request swallowed error:", err);
    }
  }

  clearTenantSession();
}

/**
 * 2.1.11 Update Password
 * PUT /auth/update-password
 * Auth: Bearer token
 * Body: { currentPassword, newPassword, confirmNewPassword }
 */
export async function updatePassword({ currentPassword, newPassword, confirmNewPassword }) {
  const ruleErr = validatePassword(newPassword);
  if (ruleErr) throw new Error(ruleErr);
  if (newPassword !== confirmNewPassword) throw new Error("Passwords do not match.");

  const res = await tenantFetch(`${API_BASE}/auth/update-password`, {
    method: "PUT",
    body: JSON.stringify({ currentPassword, newPassword, confirmNewPassword }),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Password update failed (${res.status})`);
  return json?.data || true;
}

/**
 * 2.1.12 Forgot Password — Start
 * POST /auth/forgot-password/start
 * Body: { userType: 0, identifier: "+234...", estateId: "..." }
 * userType: 0 = Tenant, 1 = Primary Occupant
 */
export async function forgotPasswordStart({ userType = 0, identifier, estateId }) {
  const normId = identifier.includes("@") ? identifier.trim() : normalizePhoneNumber(identifier);
  const estate = estateId || getTenantEstateId();
  if (!estate) throw new Error("Estate ID is required. Please provide your Estate ID.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/forgot-password/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      userType: Number(userType),
      identifier: normId,
      estateId: estate.trim(),
    }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Forgot password request failed (${res.status})`);
  return json?.data || json;
}

/**
 * 2.1.13 Forgot Password — Verify OTP
 * POST /auth/forgot-password/verify-otp
 * Body: { resetRequestId, otp }
 */
export async function forgotPasswordVerifyOtp({ resetRequestId, otp }) {
  const res = await fetchWithTimeout(`${API_BASE}/auth/forgot-password/verify-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ resetRequestId, otp: otp.trim() }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Code verification failed (${res.status})`);
  return json?.data || json;
}

/**
 * 2.1.14 Forgot Password — Reset
 * POST /auth/forgot-password/reset
 * Body: { resetRequestId, resetToken, newPassword, confirmNewPassword }
 */
export async function forgotPasswordReset({ resetRequestId, resetToken, newPassword, confirmNewPassword }) {
  const ruleErr = validatePassword(newPassword);
  if (ruleErr) throw new Error(ruleErr);
  if (newPassword !== confirmNewPassword) throw new Error("Passwords do not match.");

  const res = await fetchWithTimeout(`${API_BASE}/auth/forgot-password/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ resetRequestId, resetToken, newPassword, confirmNewPassword }),
  }, 15000);

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Password reset failed (${res.status})`);
  return json?.data || json;
}

// ───────────────────────────────────────────────────────────────
// 2.2 TENANT PROFILE & USER ENDPOINTS
// ───────────────────────────────────────────────────────────────

/**
 * 2.2.1 Get Profile Details
 * GET /tenants/{tenantId}/details or /primary-occupants/{id}/details
 * Auth: Bearer token
 */
export async function getProfileDetails(id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/details`
    : `${API_BASE}/tenants/${id}/details`;

  let res = await tenantFetch(endpoint);
  if (isPo && res.status === 404) {
    res = await tenantFetch(`${API_BASE}/primary-occupants/${id}`);
  }
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to fetch profile (${res.status})`));
  return json?.data || json;
}

/**
 * 2.2.2 Update Profile
 * PUT /tenants/{tenantId} or /primary-occupants/{id}
 * Auth: Bearer token
 */
export async function updateProfile(profileData, id = getTenantId()) {
  const endpoint = isPrimaryOccupant()
    ? `${API_BASE}/primary-occupants/${id}`
    : `${API_BASE}/tenants/${id}`;

  const payload = {
    firstName:   profileData.firstName?.trim(),
    lastName:    profileData.lastName?.trim(),
    email:       profileData.email?.trim(),
    phoneNumber: normalizePhoneNumber(profileData.phoneNumber),
    imageUrl:    profileData.imageUrl || "",
  };

  if (!isPrimaryOccupant() && profileData.occupation !== undefined) {
    payload.occupation = profileData.occupation;
  }

  const res = await tenantFetch(endpoint, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to update profile (${res.status})`);

  // Update local session data with newly updated details
  const prevSession = getTenantSession();
  if (prevSession) {
    setTenantSession({
      ...prevSession,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      phoneNumber: payload.phoneNumber,
      imageUrl: payload.imageUrl,
    });
  }

  return json?.data || true;
}

/**
 * 2.2.3 Upload Profile Image / File
 * POST /tenants/{tenantId}/uploads or /primary-occupants/{id}/uploads
 * Auth: Bearer token, multipart/form-data
 * Returns uploaded URL string
 */
export async function uploadProfileImage(file, id = getTenantId()) {
  const endpoint = isPrimaryOccupant()
    ? `${API_BASE}/primary-occupants/${id}/uploads`
    : `${API_BASE}/tenants/${id}/uploads`;

  const formData = new FormData();
  formData.append("file", file);

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: formData,
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Image upload failed (${res.status})`);
  return json?.data || "";
}

// ───────────────────────────────────────────────────────────────
// 2.2.4 BUILDINGS & 2.3 RENT ENDPOINTS
// ───────────────────────────────────────────────────────────────

/**
 * 2.2.4 Get Assigned Buildings
 * Screen: MaintenanceRequestScreen (building selector), Building Management
 * Method: GET
 * Path (Tenant): /tenants/{tenantId}/buildings/assigned?pageIndex=1&pageSize=100
 * Path (Primary Occupant): /primary-occupants/{id}/buildings/assigned
 * Response: { data: { data: [ ... ] } }
 */
export async function getAssignedBuildings({ pageIndex = 1, pageSize = 100, search = "", id = getTenantId() } = {}) {
  const isPo = isPrimaryOccupant();
  const params = new URLSearchParams(isPo ? {} : { pageIndex: String(pageIndex), pageSize: String(pageSize) });
  if (search) params.set("search", search);
  const query = params.toString() ? `?${params.toString()}` : "";
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/buildings/assigned${query}`
    : `${API_BASE}/tenants/${id}/buildings/assigned${query}`;

  const res = await tenantFetch(endpoint);
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch assigned buildings (${res.status})`);
  const data = json?.data ?? json;

  // Primary Occupants receive a single assignment object rather than a page of them.
  if (isPo) {
    const single = data && !Array.isArray(data) && typeof data === "object" && !Array.isArray(data.data) && !Array.isArray(data.items)
      ? data
      : null;
    if (single) {
      return { data: [{ ...single, number: single.number || single.buildingNumber }], totalCount: 1 };
    }
  }
  return data;
}

/**
 * 2.3.1 Get Active Rent Config
 * Screen: HomeScreen (rent card), RentPaymentScreen
 * Method: GET
 * Path (Tenant): /tenants/{tenantId}/buildings/{buildingId}/active-rent
 * Path (Primary Occupant): /primary-occupants/{id}/buildings/{buildingId}/active-rent
 * Response: { data: { rentAmount: 500000.00 } }
 */
export async function getActiveRentConfig(buildingId, id = getTenantId()) {
  if (!buildingId) throw new Error("Building ID is required.");
  const isPo = isPrimaryOccupant();
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/buildings/${buildingId}/active-rent`
    : `${API_BASE}/tenants/${id}/buildings/${buildingId}/active-rent`;

  const res = await tenantFetch(endpoint);
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch active rent config (${res.status})`);
  return json?.data || json;
}

/**
 * 2.3.2 Get Rent Invoices (Tenant Only)
 * Screen: HomeScreen, RentPaymentScreen
 * Method: GET
 * Path: /tenants/{tenantId}/rent-invoices?pageIndex=1&pageSize=20
 * Response: { data: { data: [ ... ] } }
 */
export async function getRentInvoices({ pageIndex = 1, pageSize = 20, status = "", id = getTenantId() } = {}) {
  const params = new URLSearchParams({
    pageIndex: String(pageIndex),
    pageSize: String(pageSize),
    ...(status && status !== "all" ? { status } : {}),
  });

  const endpoint = `${API_BASE}/tenants/${id}/rent-invoices?${params.toString()}`;
  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch rent invoices (${res.status})`);
  return json?.data || json;
}

/**
 * 2.3.3 Start Rent Payment
 * Screen: RentPaymentScreen
 * Method: POST
 * Path: /rent-invoices/{rentInvoiceId}/payments/start
 * Request body: {}
 * Response (200): { data: { paymentReference, transactionCharge, paymentTotalAmount } }
 * Client builds checkout URL: https://secure.payu.co.za/rpp.do?PayUReference={paymentReference}
 */
export async function startRentPayment(rentInvoiceId) {
  if (!rentInvoiceId) throw new Error("Rent invoice ID is required.");

  const endpoint = `${API_BASE}/rent-invoices/${rentInvoiceId}/payments/start`;
  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify({}),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to start payment (${res.status})`);

  const data = json?.data || json;
  const paymentReference = data?.paymentReference || "";
  const checkoutUrl = paymentReference
    ? `https://secure.payu.co.za/rpp.do?PayUReference=${paymentReference}`
    : null;

  return {
    ...data,
    checkoutUrl,
  };
}

/**
 * 2.3.4 Verify Rent Payment
 * Screen: RentPaymentScreen (WebView callback)
 * Method: POST
 * Path: /rent-payments/verify
 * Request body: { gatewayReference, merchantReference }
 * Response: 200 -> payment confirmed. Any other status -> exception.
 */
export async function verifyRentPayment({ gatewayReference, merchantReference }) {
  if (!merchantReference) throw new Error("Merchant reference is required for payment verification.");

  const endpoint = `${API_BASE}/rent-payments/verify`;
  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify({
      gatewayReference: gatewayReference || "",
      merchantReference: String(merchantReference).trim(),
    }),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) {
    throw new Error(json?.message || json?.error || `Payment verification failed (${res.status})`);
  }
  return json?.data || true;
}

// ───────────────────────────────────────────────────────────────
// 2.4 PAYMENT HISTORY
// ───────────────────────────────────────────────────────────────

/**
 * 2.4.1 Get Payment History (Tenant only)
 * GET /tenants/{tenantId}/payments
 * Query: pageIndex, pageSize, status, search, sortBy, startDate, endDate
 */
export async function getPaymentHistory({
  pageIndex = 0,
  pageSize = 20,
  status = "",
  search = "",
  sortBy = "",
  startDate = "",
  endDate = "",
  id = getTenantId(),
} = {}) {
  const params = new URLSearchParams({
    pageIndex: String(pageIndex),
    pageSize: String(pageSize),
    ...(status && status !== "all" ? { status } : {}),
    ...(search ? { search } : {}),
    ...(sortBy ? { sortBy } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  });

  const endpoint = `${API_BASE}/tenants/${id}/payments?${params.toString()}`;
  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to load payment history (${res.status})`);
  return json?.data || json;
}

// ───────────────────────────────────────────────────────────────
// 2.5 MAINTENANCE REQUESTS
// ───────────────────────────────────────────────────────────────

/**
 * 2.5.1 Get Maintenance Categories
 * GET /tenants/{tenantId}/maintenance-request-categories or /primary-occupants/{id}/maintenance-request-categories
 */
export async function getMaintenanceCategories(id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/maintenance-request-categories`
    : `${API_BASE}/tenants/${id}/maintenance-request-categories`;

  const res = await tenantFetch(endpoint);
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to load maintenance categories (${res.status})`);
  return json?.data || json;
}

/**
 * 2.5.2 Get Maintenance Requests (list)
 * GET /tenants/{tenantId}/maintenance-requests or /primary-occupants/{id}/maintenance-requests
 */
export async function getMaintenanceRequests({
  pageIndex = 1,
  pageSize = 20,
  search = "",
  status = "",
  id = getTenantId(),
} = {}) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const params = new URLSearchParams({
    pageIndex: String(pageIndex),
    pageSize: String(pageSize),
    ...(status && status !== "all" ? { status } : {}),
    ...(search ? { search } : {}),
  });

  const endpoint = `${API_BASE}/${rootPath}/maintenance-requests?${params.toString()}`;
  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to load maintenance requests (${res.status})`);
  return json?.data || json;
}

/**
 * 2.5.3 Submit Maintenance Request
 * POST /tenants/{tenantId}/maintenance-request (singular for Tenant)
 * POST /primary-occupants/{id}/maintenance-requests (plural for Primary Occupant)
 */
export async function submitMaintenanceRequest(data, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/maintenance-requests`
    : `${API_BASE}/tenants/${id}/maintenance-request`;

  let priorityVal = 1; // Default Normal
  if (typeof data.priority === "number") {
    priorityVal = data.priority;
  } else if (typeof data.priorityLevel === "string") {
    const idx = PRIORITY_LABELS.indexOf(normalizePriority(data.priorityLevel));
    if (idx >= 0) priorityVal = idx;
  }

  const payload = {
    title: data.title?.trim(),
    estateId: data.estateId || getTenantEstateId(),
    categoryId: data.categoryId,
    category: data.category || data.categoryId,
    buildingId: data.buildingId,
    description: data.description?.trim(),
    priority: priorityVal,
    fileUploadUrls: data.fileUploadUrls || data.uploadedFileUrls || [],
  };

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to submit maintenance request (${res.status})`);
  return json?.data || json;
}

/**
 * 2.5.5 Get Communication Notes
 * GET /tenants/{tenantId}/maintenance-requests/{requestId}/communication-notes
 */
export async function getCommunicationNotes(requestId, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/maintenance-requests/${requestId}/communication-notes`;

  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch communication notes (${res.status})`);
  return json?.data || json;
}

/**
 * 2.5.6 Add Communication Note
 * POST /tenants/{tenantId}/maintenance-requests/{requestId}/communication-notes
 */
export async function addCommunicationNote(requestId, { message, uploadedFileUrls = [] }, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/maintenance-requests/${requestId}/communication-notes`;

  const payload = {
    message: String(message || "").trim(),
    estateId: getTenantEstateId(),
    uploadedFileUrls: uploadedFileUrls || [],
  };

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to add communication note (${res.status})`);
  return json?.data || json;
}

// ───────────────────────────────────────────────────────────────
// 2.6 VISITOR MANAGEMENT (DEPENDENTS & ACCESS CODES)
// ───────────────────────────────────────────────────────────────

/**
 * 2.6.1 Get Dependents (paginated)
 * GET /tenants/{tenantId}/dependents or /primary-occupants/{id}/dependents
 */
export async function getDependents({
  pageIndex = 0,
  pageSize = 20,
  search = "",
  id = getTenantId(),
} = {}) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const params = new URLSearchParams({
    pageIndex: String(pageIndex),
    pageSize: String(pageSize),
    ...(search ? { search } : {}),
  });

  const endpoint = `${API_BASE}/${rootPath}/dependents?${params.toString()}`;
  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch dependents (${res.status})`);
  return json?.data || json;
}

/**
 * Visit history: gate check-ins/outs for the signed-in resident's household.
 * GET /tenants/{tenantId}/access-logs or /primary-occupants/{id}/access-logs
 * Filters: dependentId, isActiveVisit, isOverstay, isPermanentCode, search, startDate, endDate, sortBy.
 */
export async function getAccessLogs({
  pageIndex = 1,
  pageSize = 20,
  id = getTenantId(),
  ...filters
} = {}) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const params = new URLSearchParams({ pageIndex: String(pageIndex), pageSize: String(pageSize) });
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });

  const res = await tenantFetch(`${API_BASE}/${rootPath}/access-logs?${params.toString()}`);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to fetch visit history (${res.status})`);
  const page = json?.data || json || {};
  return { data: Array.isArray(page.data) ? page.data : [], totalCount: page.totalCount ?? 0 };
}

/**
 * 2.6.3 Add Dependent (without access code)
 * POST /tenants/{tenantId}/dependents or /primary-occupants/{id}/dependents
 */
export async function addDependent(data, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/dependents`;

  const depType = toDependentType(data.dependentType, 0);

  const payload = {
    firstName: data.firstName?.trim(),
    lastName: data.lastName?.trim(),
    phoneNumber: normalizePhoneNumber(data.phoneNumber),
    email: data.email?.trim() || "",
    imageUrl: data.imageUrl || "",
    dependentType: depType,
    notes: data.notes || data.relationship || "",
  };

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to add dependent (${res.status})`));
  return json?.data || json;
}

/**
 * 2.6.9 Get Dependents by Access Type (Permanent / Temporary tabs)
 * Returns the set of dependent ids that currently hold a permanent access code.
 */
async function getPermanentDependentIds(id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const endpoint = isPo
    ? `${API_BASE}/primary-occupants/${id}/access-codes/active`
    : `${API_BASE}/tenants/${id}/dependents/access-codes/permanent?hasPermanent=true&pageIndex=0&pageSize=100`;
  const res = await tenantFetch(endpoint);
  if (!res.ok) return new Set();
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  const list = parseArrayData(json);
  return new Set(
    list
      .filter((d) => (isPo ? d.isPermanent : true))
      .map((d) => d.dependentId || d.id)
      .filter(Boolean)
  );
}

/**
 * Dependents list enriched for display: access code, QR url, permanent flag and designation.
 * Resolves to { data: [...], totalCount } so callers can reuse parseArrayData.
 */
export async function getDependentsWithAccess(opts = {}) {
  const id = opts.id || getTenantId();
  const [page, permanentIds] = await Promise.all([
    getDependents({ ...opts, id }),
    getPermanentDependentIds(id).catch(() => new Set()),
  ]);
  const items = parseArrayData({ data: page }).map((d) => {
    const depId = d.dependentId || d.id;
    return {
      ...d,
      designation: d.notes || enumOf(d, "dependentType") || "",
      code: d.currentActiveAccessCode || "",
      qrUrl: d.currentActiveAccessCodeQrUrl || "",
      isPermanent: permanentIds.has(depId),
    };
  });
  return { data: items, totalCount: page?.totalCount ?? page?.total ?? page?.data?.totalCount ?? items.length };
}

/**
 * Uploads a locally picked image (data URL) and returns its hosted URL.
 * Already-hosted URLs pass through; Primary Occupants have no upload endpoint and get "".
 */
export async function uploadImageIfNeeded(image, id = getTenantId()) {
  if (!image) return "";
  if (!String(image).startsWith("data:")) return image;
  if (isPrimaryOccupant()) return "";
  const blob = await (await fetch(image)).blob();
  const file = new File([blob], `upload.${(blob.type.split("/")[1] || "png").replace("jpeg", "jpg")}`, { type: blob.type });
  return uploadProfileImage(file, id);
}

/**
 * 2.6.4 Update Dependent
 */
export async function updateDependent(dependentId, data, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const payload = {
    firstName: data.firstName?.trim(),
    lastName: data.lastName?.trim(),
    phoneNumber: normalizePhoneNumber(data.phoneNumber),
    email: data.email?.trim() || "",
    imageUrl: data.imageUrl || "",
    dependentType: toDependentType(data.dependentType, 0),
    notes: data.notes || "",
  };
  const res = await tenantFetch(`${API_BASE}/${rootPath}/dependents/${dependentId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to update dependent (${res.status})`));
  return json?.data || true;
}

/**
 * 2.6.5 Delete Dependent
 */
export async function deleteDependent(dependentId, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const res = await tenantFetch(`${API_BASE}/${rootPath}/dependents/${dependentId}`, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text ? safeJsonParse(text) : null, `Failed to delete dependent (${res.status})`));
  }
  return true;
}

/**
 * 2.6.6 Create Dependent with Access Code (combined)
 * POST /tenants/{tenantId}/dependents/access-codes or /primary-occupants/{id}/dependents/access-codes
 * Note: dependentType is removed before sending for Primary Occupants.
 */
export async function createDependentWithAccessCode(data, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/dependents/access-codes`;

  const depType = toDependentType(data.dependentType, 2);

  const payload = {
    firstName: data.firstName?.trim(),
    lastName: data.lastName?.trim(),
    phoneNumber: normalizePhoneNumber(data.phoneNumber),
    isAccessCodePermanent: Boolean(data.isAccessCodePermanent),
    durationValue: Number(data.durationValue || 30),
    durationUnit: Number(data.durationUnit !== undefined ? data.durationUnit : 2),
    buildingId: data.buildingId,
    ...(data.email?.trim() ? { email: data.email.trim() } : {}),
    ...(data.imageUrl ? { imageUrl: data.imageUrl } : {}),
    ...(data.notes?.trim() ? { notes: data.notes.trim() } : {}),
  };

  if (!isPo) {
    payload.dependentType = depType;
  }

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to create visitor access code (${res.status})`));
  return json?.data || json;
}

// ───────────────────────────────────────────────────────────────
// 2.7 VEHICLES (CARS)
// ───────────────────────────────────────────────────────────────

/**
 * 2.7.1 Get All Cars
 * GET /tenants/{tenantId}/cars or /primary-occupants/{id}/cars
 */
export async function getCars(id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/cars`;

  const res = await tenantFetch(endpoint);
  if (res.status === 204 || res.status === 404) return { data: [], totalCount: 0 };
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to load vehicles (${res.status})`);
  return json?.data || json;
}

/**
 * 2.7.3 Add Car
 * POST /tenants/{tenantId}/cars or /primary-occupants/{id}/cars
 */
export async function addCar(data, id = getTenantId()) {
  const isPo = isPrimaryOccupant();
  const rootPath = isPo ? `primary-occupants/${id}` : `tenants/${id}`;
  const endpoint = `${API_BASE}/${rootPath}/cars`;

  const payload = {
    manufacturer: data.manufacturer?.trim(),
    make: data.make?.trim() || data.manufacturer?.trim(),
    model: data.model?.trim(),
    year: Number(data.year) || new Date().getFullYear(),
    color: data.color?.trim(),
    licensePlate: data.licensePlate?.trim(),
    ...(data.imageUrl ? { imageUrl: data.imageUrl } : {}),
  };

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to add vehicle (${res.status})`);
  return json?.data || json;
}

/**
 * 2.7.2 Get Car by ID (includes assignedDependents)
 */
export async function getCarById(carId, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const res = await tenantFetch(`${API_BASE}/${rootPath}/cars/${carId}`);
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to load vehicle (${res.status})`));
  return json?.data || json;
}

/**
 * 2.7.4 Update Car
 */
export async function updateCar(carId, data, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const payload = {
    manufacturer: data.manufacturer?.trim(),
    make: data.make?.trim() || data.manufacturer?.trim(),
    model: data.model?.trim(),
    year: Number(data.year) || new Date().getFullYear(),
    color: data.color?.trim(),
    licensePlate: data.licensePlate?.trim(),
    ...(data.imageUrl ? { imageUrl: data.imageUrl } : {}),
  };
  const res = await tenantFetch(`${API_BASE}/${rootPath}/cars/${carId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to update vehicle (${res.status})`));
  return json?.data || json;
}

/**
 * 2.7.5 Delete Car
 */
export async function deleteCar(carId, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const res = await tenantFetch(`${API_BASE}/${rootPath}/cars/${carId}`, { method: "DELETE" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text ? safeJsonParse(text) : null, `Failed to delete vehicle (${res.status})`));
  }
  return true;
}

/**
 * 2.7.6 / 2.7.7 Assign or unassign a dependent on a car
 */
export async function assignDependentToCar(carId, dependentId, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const res = await tenantFetch(`${API_BASE}/${rootPath}/cars/${carId}/dependents/${dependentId}/assign`, {
    method: "POST",
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text ? safeJsonParse(text) : null, "Failed to assign occupant."));
  }
  return true;
}

export async function unassignDependentFromCar(carId, dependentId, id = getTenantId()) {
  const rootPath = isPrimaryOccupant() ? `primary-occupants/${id}` : `tenants/${id}`;
  const res = await tenantFetch(`${API_BASE}/${rootPath}/cars/${carId}/dependents/${dependentId}/unassign`, {
    method: "PATCH",
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(extractErrorMessage(text ? safeJsonParse(text) : null, "Failed to remove occupant."));
  }
  return true;
}

/**
 * 2.7.9 / 2.7.10 Car access credential (gate code + QR) — tenant only
 * GET  /tenants/{tenantId}/cars/{carId}/access-credential
 * POST /tenants/{tenantId}/cars/{carId}/access-credential/rotate
 * Returns { credentialId, carId, code, qrCodeFileUrl, issuedAt } or null when none is issued.
 */
function normalizeCarCredential(json) {
  const raw = json?.data ?? null;
  if (!raw) return null;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    return parsed && typeof parsed === "object" ? parsed : { code: raw };
  }
  return raw.accessCredential || raw;
}

export async function getCarAccessCredential(carId, id = getTenantId()) {
  const res = await tenantFetch(`${API_BASE}/tenants/${id}/cars/${carId}/access-credential`);
  if (res.status === 204 || res.status === 404) return null;
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to load access credential (${res.status})`));
  return normalizeCarCredential(json);
}

export async function rotateCarAccessCredential(carId, id = getTenantId()) {
  const res = await tenantFetch(`${API_BASE}/tenants/${id}/cars/${carId}/access-credential/rotate`, {
    method: "POST",
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Failed to rotate access code (${res.status})`));
  return normalizeCarCredential(json);
}

/**
 * 2.7.8 Generate Car Image (AI)
 * POST /tenants/{tenantId}/cars/generate-image (Tenant only)
 */
export async function generateCarImage(data, id = getTenantId()) {
  if (isPrimaryOccupant()) return null; // Tenant-only endpoint
  const endpoint = `${API_BASE}/tenants/${id}/cars/generate-image`;

  const payload = {
    manufacturer: data.manufacturer?.trim(),
    make: data.make?.trim() || data.manufacturer?.trim(),
    model: data.model?.trim(),
    year: Number(data.year) || new Date().getFullYear(),
    color: data.color?.trim(),
  };

  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to generate vehicle image (${res.status})`);
  return json?.imageUrl || json?.data?.imageUrl || json?.data || text;
}

// ───────────────────────────────────────────────────────────────
// 2.8 FOUNDATION SUPPORT (DONATIONS)
// ───────────────────────────────────────────────────────────────

/**
 * 2.8.1 Start Foundation Support Payment
 * POST /foundation-support/payments/start
 * Body: { tenantId, amount }
 * Response: { data: { paymentReference } }
 */
export async function startFoundationSupportPayment({ amount, tenantId = getTenantId() }) {
  if (!amount || Number(amount) <= 0) throw new Error("Please specify a valid donation amount.");

  const endpoint = `${API_BASE}/foundation-support/payments/start`;
  const res = await tenantFetch(endpoint, {
    method: "POST",
    body: JSON.stringify({
      tenantId,
      amount: Number(amount),
    }),
  });

  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || json?.error || `Failed to initiate donation (${res.status})`);

  const data = json?.data || json;
  const paymentReference = data?.paymentReference || "";
  const checkoutUrl = paymentReference
    ? `https://secure.payu.co.za/rpp.do?PayUReference=${paymentReference}`
    : null;

  return {
    ...data,
    paymentReference,
    checkoutUrl,
  };
}

/**
 * 2.8.2 Verify Foundation Support Payment
 * POST /foundation-support/payments/verify
 */
export async function verifyFoundationSupportPayment({ gatewayReference, merchantReference }) {
  if (!merchantReference) throw new Error("Merchant reference is required for payment verification.");
  const res = await tenantFetch(`${API_BASE}/foundation-support/payments/verify`, {
    method: "POST",
    body: JSON.stringify({
      gatewayReference: gatewayReference || "",
      merchantReference: String(merchantReference).trim(),
    }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(extractErrorMessage(json, `Donation verification failed (${res.status})`));
  return json?.data || true;
}

// ───────────────────────────────────────────────────────────────
// AUTHENTICATED FETCH WITH AUTO-REFRESH
// ───────────────────────────────────────────────────────────────
export async function tenantFetch(url, options = {}) {
  // Auto-refresh if token is within 60s of expiry
  const session = getTenantSession();
  if (session?.accessTokenExpiresAt) {
    const expiresMs = new Date(session.accessTokenExpiresAt).getTime();
    if (expiresMs - Date.now() < 60000) {
      try {
        await refreshTenantToken();
      } catch (err) {
        console.warn("Pre-emptive token refresh failed:", err);
      }
    }
  }

  // ── Real network fetch ────────────────────────────────────
  const token = getTenantAccessToken();
  const method = (options.method || "GET").toUpperCase();
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;

  let body = options.body;
  if (body !== undefined && body !== null && typeof body === "object" && !isFormData) {
    body = JSON.stringify(body);
  }

  const hasBody = body !== undefined && body !== null && body !== "" && method !== "GET" && method !== "HEAD";

  const headers = {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(hasBody && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers || {}),
  };

  // Strip Content-Type if there is no body or if method is GET/HEAD to prevent ASP.NET Core deserializer error
  if (!hasBody || method === "GET" || method === "HEAD") {
    delete headers["Content-Type"];
    delete headers["content-type"];
  }

  let res = await fetch(url, { ...options, body, headers });

  // 1.2 Auto-refresh & Global 401 Handling
  if (res.status === 401) {
    const clone = res.clone();
    const text = await clone.text().catch(() => "");
    const json = text ? safeJsonParse(text) : null;
    const msg = (json?.message || text || "").toLowerCase();
    const isValidation = msg.includes("invalid credential") || msg.includes("not found") || msg.includes("missing") || msg.includes("required");

    if (!isValidation) {
      try {
        const refreshed = await refreshTenantToken();
        const newHeaders = { ...headers, Authorization: `Bearer ${refreshed.accessToken}` };
        res = await fetch(url, { ...options, body, headers: newHeaders });
      } catch {
        clearTenantSession();
        window.location.href = "/sign-in";
        throw new Error("Session expired. Please sign in again.");
      }
    }
  }

  return res;
}

// ─── Route guard helper ───────────────────────────────────────
export function isTenantAuthenticated() {
  const s = getTenantSession();
  if (!s?.accessToken) return false;
  const remember = localStorage.getItem("zangon_tenant_remember_me");
  if (remember === "false") {
    const active = sessionStorage.getItem("zangon_tenant_session_active");
    if (!active) { clearTenantSession(); return false; }
  }
  return true;
}
